import { getUrlWithVariant } from "@shopify/theme-product-form";
import isMobile from "is-mobile";
import { enableLinksWithin, syncTabIndexesOnFlickity } from "@/lib/a11y";

import { add, listen, qs, qsa, remove, toggle } from "@fluorescent/dom";
import { cart, formatMoney, getProduct, section } from "@/neon";
import { emit, on } from "@/neon/events";

import Accordions from "@/lib/accordion";
import Lightbox from "@/lib/lightbox";
import Media from "@/lib/media";
import OptionButtons from "@/lib/option-buttons";
import ProductForm from "@/lib/product-form";
import storeAvailability from "@/lib/store-availability";
import { updateUnitPrices } from "@/lib/unit-pricing";
import { dispatchCustomEvent, CustomEvents } from "@/lib/dispatch-custom-event";

import {
  updateSku,
  updateBuyButton,
  inventoryCounter,
  informationPopup,
} from "@/lib/product";

const selectors = {
  form: "[data-product-form]",
  addToCart: "[data-add-to-cart]",
  price: "[data-price]",
  comparePrice: "[data-compare-price]",
  slide: "[data-slide]",
  slider: "[data-slider]",
  nav: "[data-move-slider]",
  variantSelect: "[data-variant-select]",
  optionById: id => `[value='${id}']`,
  storeAvailability: "[data-store-availability-container]",
  quantityError: "[data-quantity-error]",
};

section("product", {
  productForm: null,

  onLoad() {
    const { productHasOnlyDefaultVariant } = this.container.dataset;
    this.formElement = qs(selectors.form, this.container);
    this.quantityError = qs(selectors.quantityError, this.container);

    this.isFeaturedProduct =
      this.container.classList.contains("featured-product");
    const { productHandle } = this.formElement.dataset;

    this.storeAvailabilityContainer = this.formElement.querySelector(
      selectors.storeAvailability
    );
    this.availability = null;

    this.accordions = Accordions(qsa(".accordion", this.container));

    const images = qsa(".media__image[data-open]", this.container);

    const viewInYourSpaceButton = qs("[data-in-your-space]", this.container);
    viewInYourSpaceButton &&
      toggle(viewInYourSpaceButton, "visible", isMobile());

    const product = getProduct(productHandle);
    product(data => {
      this.productForm = ProductForm(this.formElement, data, {
        onOptionChange: e => this.onOptionChange(e),
        onFormSubmit: e => this.onFormSubmit(e),
      });

      // Surface pickup
      const variant = this.productForm.getVariant();

      if (this.storeAvailabilityContainer && variant) {
        this.availability = storeAvailability(
          this.storeAvailabilityContainer,
          data,
          variant,
          { hideVariantTitle: productHasOnlyDefaultVariant === "true" }
        );
      }

      const productInventoryJson = qs(
        "[data-product-inventory-json]",
        this.container
      );

      if (productInventoryJson) {
        const jsonData = JSON.parse(productInventoryJson.innerHTML);
        const variantsInventories = jsonData.inventory;

        if (variantsInventories) {
          const config = {
            id: variant.id,
            variantsInventories,
          };
          this.inventoryCounter = inventoryCounter(this.container, config);
        }
      }
    });

    const qtyButtons = qsa("[data-change]", this.container);

    if (qtyButtons.length) {
      this.qtyButtonClick = listen(qtyButtons, "click", e => {
        e.preventDefault();
        const { change } = e.currentTarget.dataset;
        const input = qs("input", e.currentTarget.parentNode);
        if (change === "increment") {
          input.value >= 0 && input.value++;
        } else if (change === "decrement") {
          input.value > 0 && input.value--;
        }
      });
    }

    this.optionButtons = OptionButtons(
      qsa("[data-option-buttons]", this.container)
    );

    this.informationPopup = informationPopup(this.container);

    this.images = qsa("[data-media]", this.container);
    this.mediaContainer = qs(".product__media-container", this.container);

    if (this.mediaContainer) {
      this.media = Media(this.mediaContainer);
    }

    if (this.isFeaturedProduct) {
      return; // Everything else here is product page specific
    }

    this.lightbox = Lightbox(qs("[data-lightbox]", this.container));
    this.imageClick = listen(images, "click", e => {
      e.preventDefault();
      this.media && this.media.pauseActiveMedia();
      this.lightbox.open(e.currentTarget.dataset.open);
    });

    const slider = qs(selectors.slider, this.container);

    const previousButton = qs("[data-prev]", this.container);
    const nextButton = qs("[data-next]", this.container);

    import(flu.chunks.flickity).then(({ Flickity }) => {
      // Avoid momentary collapse as flickity initializes by setting a min height
      // because flickity is set to "adaptiveHeight" we don't have to undo this value
      let container = this.mediaContainer,
        containerHeight = container.offsetHeight;
      container.style.minHeight = `${containerHeight}px`;

      this.flkty = new Flickity(slider, {
        adaptiveHeight: true,
        cellSelector: selectors.slide,
        initialIndex: ".initial",
        pageDots: false,
        prevNextButtons: false,
        watchCSS: true,
        wrapAround: true,
        on: {
          ready: function () {
            var flkty = Flickity.data(slider); // This is a pain but we can't access the instance within here
            syncTabIndexesOnFlickity(flkty);
            nextButton && add(nextButton, "visible");
          },
        },
      });

      this.flkty.on("deactivate", () => {
        const slides = qsa(selectors.slide, this.container);
        slides.forEach(slide => {
          enableLinksWithin(slide);
        });
      });

      this.flkty.on("change", () => {
        add(previousButton, "visible");
        this.media && this.media.pauseActiveMedia();

        syncTabIndexesOnFlickity(this.flkty);

        const newImageMedia = qs(".media", this.flkty.selectedElement);

        if (viewInYourSpaceButton) {
          if (newImageMedia.dataset.mediaType === "model") {
            viewInYourSpaceButton.setAttribute(
              "data-shopify-model3d-id",
              newImageMedia.dataset.mediaId
            );
          } else {
            viewInYourSpaceButton.setAttribute(
              "data-shopify-model3d-id",
              viewInYourSpaceButton.dataset.shopifyFirstModel3dId
            );
          }
        }
      });

      this.viewportResizeEvent = on("viewport-resize:small", () => {
        // Remove container minHeight on resize
        container.style.minHeight = null;
        setTimeout(() => {
          this.flkty.resize();
        }, 400);
      });

      // This is a little strange but prevents needing to check for existence of
      // pagination when the section is unloaded
      this.sliderPaginationClick = nextButton
        ? listen([previousButton, nextButton], "click", e => this.onClickNav(e))
        : () => {};
    });
  },

  onUnload() {
    this.optionButtons.destroy();
    this.productForm.destroy();
    this.flkty && this.flkty.destroy();
    this.accordions.destroy();
    this.lightbox && this.lightbox.destroy();

    this.imageClick();

    if (this.qtyButtonClick) {
      this.qtyButtonClick();
    }

    this.sliderPaginationClick();
    this.viewportResizeEvent();
  },

  // When the user changes a product option
  onOptionChange({ dataset: { variant } }) {
    // Update unit pricing
    updateUnitPrices(this.container, variant);
    updateSku(this.container, variant);

    dispatchCustomEvent(CustomEvents.productVariantChange, {
      variant: variant,
    });

    this.inventoryCounter && this.inventoryCounter.update(variant);

    if (!variant) {
      updateBuyButton(qs("[data-add-to-cart]", this.container), false);
      this.availability && this.availability.unload();
      return;
    }

    // Update URL with selected variant
    const url = getUrlWithVariant(window.location.href, variant.id);
    window.history.replaceState({ path: url }, "", url);

    // Update prices to reflect selected variant
    this.updatePrices(variant);

    // We need to set the id input manually so the Dynamic Checkout Button works
    const selectedVariantOpt = qs(
      `${selectors.variantSelect} ${selectors.optionById(variant.id)}`,
      this.container
    );
    selectedVariantOpt.selected = true;

    // We need to dispatch an event so Shopify pay knows the form has changed
    this.formElement.dispatchEvent(new Event("change"));

    // Update product availability content
    this.availability && this.availability.update(variant, true);

    if (this.isFeaturedProduct) {
      // On featured product block there is no slider only a single displayed image
      if ((variant.featured_media || {}).id) {
        this.media && this.media.pauseActiveMedia();

        // Update image
        this.images.forEach(image => {
          toggle(
            image,
            "visible",
            image.dataset.media === String(variant.featured_media.id)
          );
        });
      }
    } else if (variant.featured_media) {
      // Move screen or mobile slider to selected variants media

      if (window.matchMedia("(min-width: 40em)").matches) {
        const image = qs(
          `[data-media-id="${variant.featured_media.id}"]`,
          this.container
        );
        image && image.scrollIntoView({ behavior: "smooth", block: "center" });
      } else {
        this.flkty && this.flkty.select(variant.featured_media.position - 1);
      }
    }
  },

  // When user submits the product form
  onFormSubmit(event) {
    const { enableQuickCart } = document.body.dataset;
    if (!enableQuickCart) return;

    event.preventDefault();

    const button = qs(selectors.addToCart, this.container);
    add(button, "loading");
    add(this.quantityError, "hidden");

    cart
      .addItem(this.formElement)
      .then(({ item }) => {
        remove(button, "loading");
        dispatchCustomEvent(CustomEvents.cartItemAdded, { product: item });
        emit("cart:open", null, { flash: item.variant_id });
      })
      .catch(error => {
        remove(button, "loading");
        remove(this.quantityError, "hidden");
        console.debug("error", error);
      });
  },

  updatePrices(variant) {
    const price = qsa(selectors.price, this.container);
    const comparePrice = qsa(selectors.comparePrice, this.container);
    const buyButton = qs("[data-add-to-cart]", this.container);

    price.forEach(el => (el.innerHTML = formatMoney(variant.price)));
    comparePrice.forEach(
      el =>
        (el.innerHTML =
          variant.compare_at_price > variant.price
            ? formatMoney(variant.compare_at_price)
            : "")
    );

    updateBuyButton(buyButton, variant);
  },

  onClickNav(e) {
    e.preventDefault();

    if ("next" in e.currentTarget.dataset) {
      this.flkty && this.flkty.next();
    } else if ("prev" in e.currentTarget.dataset) {
      this.flkty && this.flkty.previous();
    }
  },
});
